/**
 * A colour as the wire spells it, and as a picker holds it.
 *
 * **`#rrggbb` while it is opaque, `rgba(r,g,b,a)` once it is not — never
 * `#rrggbbaa`.** Qt reads a nine-character hex as `#aarrggbb`, so a browser
 * writing CSS's order and the desktop reading Qt's would each draw a colour the
 * other never chose, with nothing throwing anywhere because both strings parse.
 * The server never parses a colour at all — `board-protocol.ts` caps it at 32
 * characters and passes it through — so the two clients agreeing *is* the
 * contract. `rgba(121,108,191,0.12)` is 22 characters, and
 * `relay/src/ui/boardview.h` carries this same note beside the same two
 * functions.
 *
 * An empty string is **no colour**, and for `fill` that is load-bearing rather
 * than cosmetic: `geometry.ts` hits an unfilled shape near its line and a filled
 * one anywhere inside, so a fill nobody can see would swallow every click meant
 * for whatever the shape was drawn around.
 */

export interface Ink {
  /** `#rrggbb`, lower case and always six digits: what `<input type="color">`
   *  takes and the only form it will give back. */
  hex: string;
  /** 0–1. */
  alpha: number;
}

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGBA = /^rgba?\(([^)]*)\)$/i;

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

const expand = (digits: string): string =>
  digits.length === 3
    ? `#${digits[0]!}${digits[0]!}${digits[1]!}${digits[1]!}${digits[2]!}${digits[2]!}`.toLowerCase()
    : `#${digits.toLowerCase()}`;

const byte = (n: number) => Math.min(255, Math.max(0, Math.round(n))).toString(16).padStart(2, '0');

/**
 * The wire's string as the two halves a control edits.
 *
 * `null` for an empty string and for anything unreadable — a colour is a field
 * another client wrote, so the caller decides what to fall back to rather than
 * this throwing on somebody else's typo.
 */
export function parseInk(wire: string): Ink | null {
  const s = wire.trim();
  if (s === '') return null;

  const hex = HEX.exec(s);
  if (hex) return { hex: expand(hex[1]!), alpha: 1 };

  const rgba = RGBA.exec(s);
  if (!rgba) return null;
  const parts = rgba[1]!.split(',').map((p) => Number(p.trim()));
  const [r, g, b] = parts;
  if (r === undefined || g === undefined || b === undefined) return null;
  if (![r, g, b].every((n) => Number.isFinite(n) && n >= 0 && n <= 255)) return null;
  const a = parts.length >= 4 ? parts[3]! : 1;
  return {
    hex: `#${byte(r)}${byte(g)}${byte(b)}`,
    alpha: Number.isFinite(a) ? clamp01(a) : 1
  };
}

/**
 * The string to send.
 *
 * Two decimals is the whole of the precision anybody can see, and it is what
 * keeps the string inside the server's cap. A fully transparent colour is still
 * written as `rgba(…,0)` rather than as nothing: "invisible" and "absent" are
 * different answers, and only the fill control turns the first into the second.
 */
export function inkToWire(ink: Ink): string {
  const alpha = clamp01(ink.alpha);
  const parsed = parseInk(ink.hex);
  const hex = parsed ? parsed.hex : '#000000';
  if (alpha >= 1) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${Math.round(alpha * 100) / 100})`;
}
